'use client';
import React, { useState, useEffect } from 'react';
import Button from '@/components/ui/Button';
import Dropdown from '@/components/ui/Dropdown';
import SafeHtml from '@/components/ui/SafeHtml';
import ProductInfoCards from './ProductInfoCards';
import { ProductDetail } from '@/types/product';
import { useCartTracking } from '@/hooks/useCartTracking';
import {
  productBodyProseClass,
  productBodyTextWrapperClass,
} from '@/services/seo/productSeoHtml';

interface ProductDetailsProps {
  product: ProductDetail;
  onAddToCart: (quantity: number, format: string) => void;
  sugerenciasDegustacion?: string;
  className?: string;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  onAddToCart,
  sugerenciasDegustacion,
  className = '',
}) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState('Unidad');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const { trackAddToCartEvent } = useCartTracking();

  const handleQuantityChange = (change: number) => {
    setQuantity((prev) => Math.max(1, prev + change));
  };

  // Inicializar valores por defecto para cada grupo de opciones
  useEffect(() => {
    if (product.productOptions) {
      const initialOptions: Record<string, string> = {};
      const groupNames = [...new Set(product.productOptions.map((option) => option.groupName))];

      groupNames.forEach((groupName) => {
        const firstOption = product.productOptions?.find(
          (option) => option.groupName === groupName
        );
        if (firstOption) {
          initialOptions[groupName] = firstOption.id.toString();
        }
      });

      setSelectedOptions(initialOptions);
    }
  }, [product.productOptions]);

  const handleOptionChange = (groupName: string, optionId: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [groupName]: optionId,
    }));
  };

  // Función para obtener los nombres de las opciones seleccionadas
  const getSelectedOptionNames = () => {
    const selectedNames: Record<string, string> = {};
    Object.entries(selectedOptions).forEach(([groupName, optionId]) => {
      const option = product.productOptions?.find(
        (opt) => opt.groupName === groupName && opt.id.toString() === optionId
      );
      if (option) {
        selectedNames[groupName] = option.name;
      }
    });
    return selectedNames;
  };

  // Función para obtener los IDs de las opciones seleccionadas
  const getSelectedOptionIds = () => {
    return Object.values(selectedOptions);
  };

  // Función para obtener las opciones completas seleccionadas
  const getSelectedOptionsComplete = () => {
    const selectedOptionsComplete: Record<string, any> = {};
    Object.entries(selectedOptions).forEach(([groupName, optionId]) => {
      const option = product.productOptions?.find(
        (opt) => opt.groupName === groupName && opt.id.toString() === optionId
      );
      if (option) {
        selectedOptionsComplete[groupName] = option;
      }
    });
    return selectedOptionsComplete;
  };

  const handleAddToCart = () => {
    const selectedOptionNames = getSelectedOptionNames();
    const variant = Object.values(selectedOptionNames).join(' - ') || 'default';

    onAddToCart(quantity, selectedFormat);
  };

  // Obtener las opciones de los grupos sin que se repitan
  const grupOptions = product.productOptions
    ?.map((option) => option.groupName)
    .filter((option, index, self) => self.indexOf(option) === index);

  const formatOptions = ['Unidad', 'Pack de 2', 'Pack de 5'];
  return (
    <div data-component-id="src/components/sections/ProductDetails.tsx:109:4" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="109" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex flex-col gap-4 flex-1 ${className}`}>
      <h1 data-component-id="src/components/sections/ProductDetails.tsx:110:6" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="110" data-component-file="ProductDetails.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-global-1%20font-tt-norms-extra-black%20font-black%20text-%5B30px%5D%20leading-9%22%7D" className="text-global-1 font-tt-norms-extra-black font-black text-[30px] leading-9">
        {product.title}
        <span data-component-id="src/components/sections/ProductDetails.tsx:112:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="112" data-component-file="ProductDetails.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22sr-only%22%7D" className="sr-only"> {product.brand}</span>
      </h1>

      <div data-component-id="src/components/sections/ProductDetails.tsx:115:6" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="115" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20justify-between%22%7D" className="flex flex-row items-center justify-between">
        <span data-component-id="src/components/sections/ProductDetails.tsx:116:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="116" data-component-file="ProductDetails.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-global-1%20font-tt-norms-light%20font-light%20text-xl%20leading-6%22%7D" className="text-global-1 font-tt-norms-light font-light text-xl leading-6">
          {product.brand}
        </span>
        {product.reference && (
          <span data-component-id="src/components/sections/ProductDetails.tsx:120:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="120" data-component-file="ProductDetails.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-global-1%20font-tt-norms%20font-normal%20text-base%20leading-5%22%2C%22textContent%22%3A%22Ref%3A%22%7D" className="text-global-1 font-tt-norms font-normal text-base leading-5">
            Ref: {product.reference}
          </span>
        )}
      </div>

      {product.shortDescription && (
        <div data-component-id="src/components/sections/ProductDetails.tsx:127:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="127" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Bvar%3AproductBodyTextWrapperClass%5D%22%7D" className={productBodyTextWrapperClass}>
          <SafeHtml data-component-id="src/components/sections/ProductDetails.tsx:128:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="128" data-component-file="ProductDetails.tsx" data-component-name="SafeHtml" data-component-content="%7B%22elementName%22%3A%22SafeHtml%22%2C%22className%22%3A%22%5Bvar%3AproductBodyProseClass%5D%22%7D" html={product.shortDescription} className={productBodyProseClass} />
        </div>
      )}

      {sugerenciasDegustacion && (
        <div data-component-id="src/components/sections/ProductDetails.tsx:133:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="133" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Bvar%3AproductBodyTextWrapperClass%5D%22%7D" className={productBodyTextWrapperClass}>
          <p data-component-id="src/components/sections/ProductDetails.tsx:134:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="134" data-component-file="ProductDetails.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-extra-black%20font-black%20text-base%20leading-5%20mb-2%22%2C%22textContent%22%3A%22Sugerencias%20de%20degustaci%C3%B3n%3A%22%7D" className="font-tt-norms-extra-black font-black text-base leading-5 mb-2">
            Sugerencias de degustación:
          </p>
          <SafeHtml data-component-id="src/components/sections/ProductDetails.tsx:137:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="137" data-component-file="ProductDetails.tsx" data-component-name="SafeHtml" data-component-content="%7B%22elementName%22%3A%22SafeHtml%22%2C%22className%22%3A%22%5Bvar%3AproductBodyProseClass%5D%22%7D" html={sugerenciasDegustacion} className={productBodyProseClass} />
        </div>
      )}

      {/* Price Section */}
      <div data-component-id="src/components/sections/ProductDetails.tsx:142:6" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="142" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-baseline%20gap-4%20mt-6%20mb-6%22%7D" className="flex flex-row items-baseline gap-4 mt-6 mb-6">
        <span data-component-id="src/components/sections/ProductDetails.tsx:143:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="143" data-component-file="ProductDetails.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-bold%20font-bold%20text-%5B31px%5D%20leading-%5B38px%5D%20text-global-1%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D" className="font-tt-norms-bold font-bold text-[31px] leading-[38px] text-global-1">
          {product.priceFinalInc.toFixed(2).replace('.', ',')}€/ud.
        </span>

        {product.priceOriginalInc &&
          product.priceOriginalInc > 0 &&
          product.priceOriginalInc !== product.priceFinalInc && (
            <span data-component-id="src/components/sections/ProductDetails.tsx:150:12" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="150" data-component-file="ProductDetails.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-bold%20font-bold%20text-%5B21px%5D%20leading-%5B26px%5D%20text-global-1%20line-through%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D" className="font-tt-norms-bold font-bold text-[21px] leading-[26px] text-global-1 line-through">
              {product.priceOriginalInc.toFixed(2).replace('.', ',')}€/ud.
            </span>
          )}
      </div>
      {/* Format and Quantity Selection */}
      <div data-component-id="src/components/sections/ProductDetails.tsx:156:6" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="156" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-3%22%7D" className="flex flex-col sm:flex-row gap-3">
        {grupOptions?.map((groupOption) => {
          const groupOptions =
            product.productOptions?.filter((option) => option.groupName === groupOption) || [];
          const dropdownOptions = groupOptions.map((option) => ({
            value: option.id.toString(),
            label: option.name,
          }));

          return (
            <Dropdown data-component-id="src/components/sections/ProductDetails.tsx:166:12" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="166" data-component-file="ProductDetails.tsx" data-component-name="Dropdown" data-component-content="%7B%22elementName%22%3A%22Dropdown%22%2C%22className%22%3A%22w-full%20sm%3Aw-%5B262px%5D%22%7D"
              key={groupOption}
              options={dropdownOptions}
              value={selectedOptions[groupOption]}
              onChange={(value) => handleOptionChange(groupOption, value)}
              className="w-full sm:w-[262px]"
              variant="brown"
            />
          );
        })}

        <div data-component-id="src/components/sections/ProductDetails.tsx:177:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="177" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20bg-%5Brgba(61%2C28%2C26%2C0.17)%5D%20rounded-%5B10px%5D%20w-full%20sm%3Aw-%5B262px%5D%20h-%5B40px%5D%22%7D" className="flex flex-row items-center bg-[rgba(61,28,26,0.17)] rounded-[10px] w-full sm:w-[262px] h-[40px]">
          <button data-component-id="src/components/sections/ProductDetails.tsx:178:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="178" data-component-file="ProductDetails.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-%5B40px%5D%20h-%5B40px%5D%22%7D"
            onClick={() => handleQuantityChange(-1)}
            className="flex items-center justify-center w-[40px] h-[40px]"
          >
            <div data-component-id="src/components/sections/ProductDetails.tsx:182:12" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="182" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-%5B10px%5D%20h-%5B2px%5D%20bg-global-1%22%7D" className="w-[10px] h-[2px] bg-global-1"></div>
          </button>
          <span data-component-id="src/components/sections/ProductDetails.tsx:184:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="184" data-component-file="ProductDetails.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex-1%20text-center%20text-global-2%20font-tt-norms%20font-medium%20text-base%20leading-5%22%7D" className="flex-1 text-center text-global-2 font-tt-norms font-medium text-base leading-5">
            {quantity}
          </span>
          <button data-component-id="src/components/sections/ProductDetails.tsx:187:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="187" data-component-file="ProductDetails.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-%5B40px%5D%20h-%5B40px%5D%22%7D"
            onClick={() => handleQuantityChange(1)}
            className="flex items-center justify-center w-[40px] h-[40px]"
          >
            <div data-component-id="src/components/sections/ProductDetails.tsx:191:12" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="191" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-%5B10px%5D%20h-%5B2px%5D%20bg-global-1%22%7D" className="w-[10px] h-[2px] bg-global-1"></div>
            <div data-component-id="src/components/sections/ProductDetails.tsx:192:12" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="192" data-component-file="ProductDetails.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-%5B2px%5D%20h-%5B10px%5D%20bg-global-1%20absolute%22%7D" className="w-[2px] h-[10px] bg-global-1 absolute"></div>
          </button>
        </div>
      </div>

      {/* Add to Cart Button */}
      <Button data-component-id="src/components/sections/ProductDetails.tsx:198:6" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="198" data-component-file="ProductDetails.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22w-full%20h-%5B50px%5D%20flex%20items-center%20justify-center%20gap-4%20bg-yellow-400%20hover%3Abg-yellow-500%20text-%5B%233D1C1A%5D%20rounded-%5B10px%5D%22%7D"
        variant="primary"
        onClick={handleAddToCart}
        className="w-full h-[50px] flex items-center justify-center gap-4 bg-yellow-400 hover:bg-yellow-500 text-[#3D1C1A] rounded-[10px]"
      >
        <svg data-component-id="src/components/sections/ProductDetails.tsx:203:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="203" data-component-file="ProductDetails.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
          width="24"
          height="23"
          viewBox="0 0 24 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path data-component-id="src/components/sections/ProductDetails.tsx:210:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="210" data-component-file="ProductDetails.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
            d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path data-component-id="src/components/sections/ProductDetails.tsx:217:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="217" data-component-file="ProductDetails.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
            d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path data-component-id="src/components/sections/ProductDetails.tsx:224:10" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="224" data-component-file="ProductDetails.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
            d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span data-component-id="src/components/sections/ProductDetails.tsx:232:8" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="232" data-component-file="ProductDetails.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms%20font-bold%20text-lg%22%2C%22textContent%22%3A%22A%C3%B1adir%20al%20carrito%22%7D" className="font-tt-norms font-bold text-lg">Añadir al carrito</span>
      </Button>

      {/* Info Cards */}
      <ProductInfoCards data-component-id="src/components/sections/ProductDetails.tsx:236:6" data-component-path="src/components/sections/ProductDetails.tsx" data-component-line="236" data-component-file="ProductDetails.tsx" data-component-name="ProductInfoCards" data-component-content="%7B%22elementName%22%3A%22ProductInfoCards%22%7D" />
    </div>
  );
};

export default ProductDetails;

/*
EJEMPLO DE USO DE LOS VALORES SELECCIONADOS:

// Para obtener los nombres de las opciones seleccionadas:
const selectedNames = getSelectedOptionNames();
// Ejemplo: { "Tamaño": "Grande", "Color": "Rojo" }

// Para obtener solo los IDs de las opciones seleccionadas:
const selectedIds = getSelectedOptionIds();
// Ejemplo: ["123", "456"]

// Para obtener las opciones completas seleccionadas:
const selectedOptionsComplete = getSelectedOptionsComplete();
// Ejemplo: { "Tamaño": { id: 123, groupName: "Tamaño", name: "Grande", ... }, ... }

// Para acceder a un valor específico:
const selectedSize = selectedOptions["Tamaño"]; // ID de la opción seleccionada
const selectedSizeName = getSelectedOptionNames()["Tamaño"]; // Nombre de la opción seleccionada
*/
